import { useEffect, useState } from "react";
import "./ScenarioConfig.scss";
import { useSelector, useDispatch } from "react-redux";
import { setScenarioMultiplier, setSelectedScenario, setScenarioId, setShowScenarioConfigDropdown, setShowGenerateScheduleConfig } from "../../../../store/UnifiedControlPlaneSlice";
import UnifiedControlPlaneService from "../../UnifiedControlPlane.service";
import { ChevronRight, CloseIcon, SatelliteGroupIcon, ScenarioConfigInfoIcon, ScenarioConfigSeparator, SettingsIcon } from "../../assets/icons/Icons";
import dayjs from "dayjs";

const ScenarioConfig = ({ mission_id }) => {
    const dispatch = useDispatch();
    const allScenarios = useSelector(
        (state) => state.unifiedControlPlane.allScenarios
    );
    const selectedScenario = useSelector(
        (state) => state.unifiedControlPlane.selectedScenario
    );
    const currentUcpConfig = useSelector((state) => state.unifiedControlPlane.currentUcpConfig)
    const showScenarioConfigDropdown = useSelector((state => state.unifiedControlPlane.showScenarioConfigDropdown));

    const [showAllScenarioList, setShowAllScenarioList] = useState(false);
    const [tempSelectedScenario, setTempSelectedScenario] = useState(null);
    const [hasScenarioChanged, setHasScenarioChanged] = useState(false);
    const [showScnearioCompletedText, setShowScenarioCompletedText] = useState(false);

    useEffect(() => {
        if (mission_id) {
            dispatch(UnifiedControlPlaneService.getCurrentUcpConfig(mission_id))
        }
    }, [dispatch, mission_id]);

    useEffect(() => {
        if (!currentUcpConfig || Object.keys(currentUcpConfig).length === 0) {
            setTempSelectedScenario(null)
            setHasScenarioChanged(false);
            return;
        }

        if (currentUcpConfig?.scenarioID && allScenarios?.length > 0) {
            const matchingScenario = allScenarios.find(scenario => scenario.scenarioId === currentUcpConfig.scenarioID);

            if (matchingScenario) {
                dispatch(setScenarioId(matchingScenario.scenarioId))
                dispatch(setSelectedScenario(matchingScenario))
                dispatch(setScenarioMultiplier(matchingScenario.sampleRate))
                setTempSelectedScenario(matchingScenario);
                setHasScenarioChanged(false)
            }
        }
    }, [dispatch, currentUcpConfig, allScenarios]);

    useEffect(() => {
        if (selectedScenario?.status === "SimulationCompleted") {
            setShowScenarioCompletedText(true)
        }
    }, [selectedScenario])

    const handleSelectScenario = (scenario) => {
        setTempSelectedScenario(scenario);
        setShowAllScenarioList(false);

        const isCurrentScenario = currentUcpConfig?.scenarioID === scenario.scenarioId;
        setHasScenarioChanged(!isCurrentScenario);
    };

    const onScenarioApplySuccess = () => {
        dispatch(setScenarioId(tempSelectedScenario.scenarioId));
        dispatch(setSelectedScenario(tempSelectedScenario));
        dispatch(setScenarioMultiplier(tempSelectedScenario?.sampleRate));
    }

    const handleApplyScenario = () => {
        if (tempSelectedScenario && hasScenarioChanged) {
            dispatch(UnifiedControlPlaneService.setCurrentUcpConfig(
                mission_id,
                tempSelectedScenario.scenarioId,
                tempSelectedScenario.scenarioName,
                onScenarioApplySuccess
            ));
            setHasScenarioChanged(false);
            dispatch(setShowScenarioConfigDropdown(false));
        }
    };

    const handleCancelConfig = () => {
        if (selectedScenario && Object.keys(selectedScenario).length > 0) {
            setTempSelectedScenario(selectedScenario);
            setHasScenarioChanged(false)
        } else {
            setTempSelectedScenario(null);
        }
        dispatch(setShowScenarioConfigDropdown(false));
    };

    const getCurrentUcpStatus = () => {
        if (selectedScenario && Object.keys(selectedScenario).length > 0 && selectedScenario?.status !== "SimulationCompleted" && currentUcpConfig?.status) {
            return currentUcpConfig.status;
        }
        return "Not Connected";
    }

    return (
        <>
            <div className="scenario-config-bar">
                <div className="config-bar-left">
                    <div className="config-bar-indicator" />
                    <div className={`config-bar-status ${getCurrentUcpStatus() === "Connected" ? "connected" : ""}`}>
                        {getCurrentUcpStatus()}
                    </div>
                </div>
                <ScenarioConfigSeparator />
                <div
                    className="config-bar-button"
                    onClick={() => {
                        dispatch(setShowScenarioConfigDropdown(!showScenarioConfigDropdown))
                        if (showAllScenarioList) {
                            setShowAllScenarioList(false)
                        }
                        dispatch(setShowGenerateScheduleConfig(false))
                    }}
                >
                    <SettingsIcon />
                </div>
            </div>
            {showScnearioCompletedText && selectedScenario?.status === "SimulationCompleted" && (
                <div className="scenario-completed-message">
                    <div className="message-header">
                        <div className="message-text header-title">SCENARIO ENDED</div>
                        <div className="header-close" onClick={() => setShowScenarioCompletedText(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="message-text message-description">
                        {selectedScenario.scenarioName} completed. Please connect satellites again to proceed.
                    </div>
                </div>
            )}
            {showScenarioConfigDropdown && (
                <div className="scenario-config-dropdown">
                    <div className="config-dropdown-header">
                        <div className="config-dropdown-title">
                            <span className="config-dropdown-title-text">Configure</span>
                        </div>
                        <div
                            className="config-dropdown-close"
                            onClick={() => dispatch(setShowScenarioConfigDropdown(false))}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="config-dropdown-body">
                        <div className="scenario-mode">
                            Simulation Mode
                        </div>
                        {!tempSelectedScenario && (
                            <div className="scenario-dropdown-banner info">
                                <div className="banner-content">
                                    <ScenarioConfigInfoIcon width={18} height={18} />
                                    <div className="banner-text">
                                        {allScenarios.length === 0 ?
                                            "There are no scenarios available. Please deploy a scenario in Simulate to use TrueTwin mode." :
                                            "Select a scenario to configure the satellite groups to be used."
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                        {currentUcpConfig?.scenarioID && hasScenarioChanged && (
                            <div className="scenario-dropdown-banner warning">
                                <div className="banner-content">
                                    <ScenarioConfigInfoIcon width={24} height={24} />
                                    <div className="banner-text">
                                        Applying this scenario will change the current configuration. Make sure to save any unsaved work before proceeding.
                                    </div>
                                </div>
                            </div>
                        )}
                        {allScenarios.length > 0 && (
                            <>
                                <div className="scenario-select-container">
                                    <div
                                        className="scenario-select-input-group"
                                        onClick={() => setShowAllScenarioList(!showAllScenarioList)}
                                    >
                                        <div className="scenario-select-text">
                                            {tempSelectedScenario ? tempSelectedScenario.scenarioName : "Select Scenario"}
                                        </div>
                                        <div className={`scenario-select-chevron ${showAllScenarioList ? "rotated" : ""}`}>
                                            <ChevronRight />
                                        </div>
                                    </div>
                                    {showAllScenarioList && (
                                        <div className="scenario-list">
                                            {allScenarios?.filter((scenario) => scenario.status !== "SimulationCompleted").map((scenario) => {
                                                const statusClassMap = {
                                                    ReadyForSimulation: "status-ready",
                                                    SimulationRunning: "status-running",
                                                };
                                                const statusTextMap = {
                                                    ReadyForSimulation: "Ready to Deploy",
                                                    SimulationRunning: "Running Scenario",
                                                };

                                                const statusClass = statusClassMap[scenario.status];
                                                const statusText = statusTextMap[scenario.status];
                                                return (
                                                    <div
                                                        key={scenario.scenarioId}
                                                        className="scenario-item"
                                                        onClick={() => handleSelectScenario(scenario)}
                                                    >
                                                        <div className="scenario-name">
                                                            {scenario.scenarioName}
                                                        </div>
                                                        <div className={`scenario-status ${statusClass}`}>
                                                            <div className="ss-text">
                                                                {statusText}
                                                            </div>
                                                            <div className="ss-indicator" />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {tempSelectedScenario && !showAllScenarioList && (
                                        <div className="scenario-details-group">
                                            <div className="scenario-details-heading">
                                                SCENARIO DETAILS
                                            </div>
                                            <div className="scneario-details-overview">
                                                <div className="scenario-overview-item">
                                                    <div className="scenario-overview-item-text scenario-overview-item-label">
                                                        Start Time
                                                    </div>
                                                    <div className="scenario-overview-item-text">
                                                        {dayjs.utc(tempSelectedScenario.simulationStartTime * 1000).format("YYYY‑MM‑DD HH:mm:ss")}
                                                    </div>
                                                </div>
                                                <div className="scenario-overview-item">
                                                    <div className="scenario-overview-item-text scenario-overview-item-label">
                                                        Duration
                                                    </div>
                                                    <div className="scenario-overview-item-text">
                                                        {tempSelectedScenario.simulationDuration} Min
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="scenario-satellite-group-container">
                                                {tempSelectedScenario?.satelliteGroups?.map((group) => (
                                                    <div
                                                        key={group.satelliteGroupId}
                                                        className="scenario-satellite-group"
                                                    >
                                                        <div className="sattelite-group-header">
                                                            <div className="satellite-group-indicator" />
                                                            <div className="satellite-group-name">
                                                                {group.satelliteGroupName}
                                                            </div>
                                                        </div>
                                                        <div className="satellite-group-info">
                                                            <div className="satellite-group-count">
                                                                {group.satelliteCount}
                                                            </div>
                                                            <div className="satellite-group-icon">
                                                                <SatelliteGroupIcon />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="config-dropdown-footer">
                        <div
                            className="scenario-action-button cancel-scenario"
                            onClick={handleCancelConfig}
                        >
                            <div className="scenario-action-button-text">Cancel</div>
                        </div>
                        <div
                            className={`scenario-action-button apply-scenario ${tempSelectedScenario && hasScenarioChanged ? "apply-scenario-active" : ""
                                }`}
                            onClick={handleApplyScenario}
                        >
                            <div
                                className={`scenario-action-button-text apply-scenario-text ${tempSelectedScenario && hasScenarioChanged ? "apply-scenario-text-active" : ""
                                    }`}
                            >
                                Apply
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
export default ScenarioConfig;